// @flow
import { EuroRounded, Percent } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import { Box, Button, FormControl, FormHelperText, FormLabel, IconButton, TextField } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { ErrorMessage } from "formik";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import { v4 as uuidv4 } from "uuid";
import FormBoxTitle from "../../../../../components/mui/app/formBoxTitle/formBoxTitle";
import { chargeValueOnTotalItemValue } from "../../../../../helpers/helpers";
import {
	formControlDefaults, numericFormatDefaults, numericFormatWithIconProps, textFieldDefaults,
} from "../../../../../helpers/theme/defaultProps";
import useUserData from "../../../../../hooks/user/useUserData";
import { ISSUES_FORM } from "../../../locales/namespaces";

type Props = {};

const Charges = (props: Props) => {
	const { t } = useTranslation(ISSUES_FORM)
	const { formik, theme, IssueId, isBelowMd } = props
	const { thousandSeparator, decimalSeparator } = useUserData()
	
	const addCharge = useCallback(() => {
		formik.setFieldValue('charges', [
			...formik.values.charges,
			{
				id: uuidv4(),
				ChargeDescription: '',
				PCTCharge: 0,
				Note: '',
			}
		])
	}, [formik])
	
	const removeCharge = useCallback((index) => {
		const updatedCharges = [...formik.values.charges]
		updatedCharges.splice(index, 1)
		formik.setFieldValue('charges', updatedCharges)
	}, [formik])
	
	return (
		<Grid
			size={12}
		>
			<FormBoxTitle
				title={t('Surcharges')}
				children={
					<Button
						onClick={addCharge}
						variant={'text'}
						startIcon={<AddIcon/>}
					>
						{t('Add')}
					</Button>
				}
			/>
			{formik.values.charges?.map((charge, chargeIndex) => {
				return (
					<Box key={charge.id} sx={{ borderLeft: `4px solid ${theme.palette.error.main}` }}>
						<Grid size={12}>
							<Box sx={{
								display: 'flex',
								justifyContent: 'flex-end',
								background: '#fff5ee',
								paddingLeft: 0.4,
								paddingRight: 0.4
							}}>
								<FormControl {...formControlDefaults}
											 sx={{ display: 'inline' }}>
									<IconButton color="error"
												onClick={() => removeCharge(chargeIndex)}>
										<ClearIcon/>
									</IconButton>
								</FormControl>
							</Box>
						</Grid>
						<Grid
							container
							spacing={1}
							sx={{ padding: 0.4, marginBottom: 0.8 }}
						>
							<Grid size={{ xs: 12, sm: 12, md: 3, lg: 3 }}>
								<FormControl
									{...formControlDefaults}
									error={
										formik.touched[`charges[${chargeIndex}].ChargeDescription`] &&
										Boolean(formik.errors[`charges[${chargeIndex}].ChargeDescription`])
									}
								>
									<FormLabel>{t('Surcharge')}</FormLabel>
									<TextField
										{...textFieldDefaults}
										name={`charges[${chargeIndex}].ChargeDescription`}
										value={charge.ChargeDescription}
										onChange={(e) => formik.setFieldValue(`charges[${chargeIndex}].ChargeDescription`, e.target.value)}
										onBlur={formik.handleBlur}
									/>
									<FormHelperText>
										<ErrorMessage name={`charges[${chargeIndex}].ChargeDescription`}/>
									</FormHelperText>
								</FormControl>
							</Grid>
							<Grid size={{ xs: 12, sm: 12, md: 2, lg: 2 }} sx={{maxWidth: isBelowMd ? '100%' : '100px'}}>
								<FormControl
									{...formControlDefaults}
									error={
										formik.touched[`charges[${chargeIndex}].PCTCharge`] &&
										Boolean(formik.errors[`charges[${chargeIndex}].PCTCharge`])
									}
								>
									<FormLabel>{t('Percentage')}</FormLabel>
									<NumericFormat
										{...numericFormatWithIconProps(Percent)}
										thousandSeparator={thousandSeparator}
										decimalSeparator={decimalSeparator}
										name={`charges[${chargeIndex}].PCTCharge`}
										value={charge.PCTCharge}
										onValueChange={(values, sourceInfo) => {
											formik.setFieldValue(`charges[${chargeIndex}].PCTCharge`, values.floatValue)
										}}
										onBlur={formik.handleBlur}
									/>
									<FormHelperText>
										<ErrorMessage name={`charges[${chargeIndex}].PCTCharge`}/>
									</FormHelperText>
								</FormControl>
							</Grid>
							<Grid size={{ xs: 12, sm: 12, md: 2, lg: 2 }} sx={{maxWidth: isBelowMd ? '100%' : '100px'}}>
								<FormControl {...formControlDefaults}>
									<FormLabel>{t('Value')}</FormLabel>
									<NumericFormat
										{...numericFormatWithIconProps(EuroRounded)}
										thousandSeparator={thousandSeparator}
										decimalSeparator={decimalSeparator}
										name={`charges[${chargeIndex}].chargeValue`}
										value={chargeValueOnTotalItemValue(charge.PCTCharge, formik.values.items)}
										disabled
									/>
								</FormControl>
							</Grid>
							<Grid size={{ xs: 12, sm: 12, md: 3, lg: 3 }}>
								<FormControl
									{...formControlDefaults}
								>
									<FormLabel>{t('Note')}</FormLabel>
									<TextField
										{...textFieldDefaults}
										name={`charges[${chargeIndex}].Note`}
										value={charge.Note}
										onChange={(e) => formik.setFieldValue(`charges[${chargeIndex}].Note`, e.target.value)}
										onBlur={formik.handleBlur}
									/>
								</FormControl>
							</Grid>
						</Grid>
					</Box>
				)
			})}
		</Grid>
	);
};

export default Charges